import FilterAlt from "@mui/icons-material/FilterAlt";
import FileDownload from "@mui/icons-material/FileDownload";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  InputAdornment,
  LinearProgress,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { DataGridPro, type GridColDef } from "@mui/x-data-grid-pro";
import Papa from "papaparse";
import { type FC, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useSupabaseCallback, useSupabaseLoader } from "../../../server/supabase/hooks";
import type { UUID } from "../../../utils/uuid";
import { serveCsv } from "../../../pages/moments/utils/serve-csv";
import { useSegmentsWithFavourites } from "../../segment/server/use-segments";
import { useEmployeeDataGridQuery } from "../hooks/use-employee-data-grid-query";
import {
  type EmployeeDataRow,
  SupabasePersonService,
  toEmployeeDataGridPage,
} from "../server/supabase-person-service";

type EmployeeRow = EmployeeDataRow["data"] & { id: UUID };

export const OptimizedEmployeeDataGridWrapper: FC<{
  type: "hris" | "csv";
  reloadCount: number;
}> = ({ type, reloadCount }) => {
  const { data: audiences, loading: audiencesLoading } = useSegmentsWithFavourites({
    is_recipient_type: true,
    include_deleted: false,
  });

  if (audiencesLoading) return <LinearProgress />;

  const defaultAudience =
    type === "hris"
      ? (audiences?.find((x) => x.name === "Current employees and upcoming new hires")?.id ??
        audiences?.find((x) => x.name === "Current employees")?.id ??
        null)
      : null;

  return (
    <OptimizedEmployeeDataGrid
      key={`${type}-${defaultAudience ?? "all"}`}
      type={type}
      reloadCount={reloadCount}
      audiences={audiences ?? []}
      defaultAudience={defaultAudience}
    />
  );
};

export const OptimizedEmployeeDataGrid: FC<{
  type: "hris" | "csv";
  reloadCount: number;
  audiences: NonNullable<ReturnType<typeof useSegmentsWithFavourites>["data"]>;
  defaultAudience: UUID | null;
}> = ({ type, reloadCount, audiences, defaultAudience }) => {
  const query = useEmployeeDataGridQuery(defaultAudience);

  const columnsState = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      void reloadCount;
      const { data, error } = await new SupabasePersonService(supabase).getEmployeeDataColumns(
        account_id,
      );
      if (error) throw error;
      return data?.map(({ key }) => key) ?? [];
    },
    [reloadCount],
  );

  const pageState = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      void reloadCount;
      const { data, error } = await new SupabasePersonService(supabase).getEmployeeDataPage(
        account_id,
        {
          cursor: query.cursor,
          pageSize: query.paginationModel.pageSize,
          segmentId: query.selectedAudience,
          sort: query.sort,
          filter: query.filter,
        },
      );
      if (error) throw error;
      return toEmployeeDataGridPage(data);
    },
    [
      query.cursor,
      query.paginationModel.pageSize,
      query.selectedAudience,
      reloadCount,
      query.sort,
      query.filter,
    ],
  );

  const columns = useMemo<GridColDef<EmployeeRow>[]>(
    () =>
      (columnsState.data ?? [])
        .filter((field) => field !== "id")
        .map((field) => ({ field, headerName: field, valueFormatter })),
    [columnsState.data],
  );
  const audience = audiences?.find((x) => x.id === query.selectedAudience);

  if (columnsState.loading) return <LinearProgress />;

  return (
    <>
      <Typography variant="h6">View/Download employees</Typography>
      <Typography variant="subtitle2" color="text.secondary">
        View and download a copy of your employee data
      </Typography>
      <Card>
        <CardContent>
          {type === "csv" && query.selectedAudience && (
            <Alert severity="warning">
              The exported CSV will only contain a subset of your records, please do not import it
              as filtered records will be deleted.
            </Alert>
          )}
          <Toolbar disableGutters>
            <EmployeeExportButton
              fields={columnsState.data ?? []}
              segmentId={query.selectedAudience}
            />
            <Box sx={{ flex: 1 }} />
            <Autocomplete
              value={audience ?? null}
              options={audiences}
              disabled={pageState.loading}
              loading={pageState.loading}
              onChange={(_, value) => query.onAudienceChange(value?.id ?? null)}
              sx={{ minWidth: "250px" }}
              getOptionLabel={(option) => option.name ?? ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  id="audience-filter"
                  placeholder="Audience"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <FilterAlt />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </Toolbar>
          <DataGridPro
            columns={columns}
            rows={pageState.data?.rows ?? []}
            rowCount={pageState.data?.rowCount ?? 0}
            loading={pageState.loading}
            pagination
            paginationMode="server"
            sortingMode="server"
            filterMode="server"
            paginationModel={query.paginationModel}
            sortModel={query.sortModel}
            filterModel={query.filterModel}
            onPaginationModelChange={(model) =>
              query.onPaginationModelChange(model, pageState.data?.nextCursor ?? null)
            }
            onSortModelChange={query.onSortModelChange}
            onFilterModelChange={query.onFilterModelChange}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>
    </>
  );
};

const EmployeeExportButton: FC<{ fields: string[]; segmentId: UUID | null }> = ({
  fields,
  segmentId,
}) => {
  const [loading, setLoading] = useState(false);
  const loadPage = useSupabaseCallback(
    async ({ supabase, account_id }, afterId: UUID | null) => {
      const { data, error } = await new SupabasePersonService(supabase).getEmployeeDataExportPage(
        account_id,
        afterId,
        1000,
        segmentId,
      );
      if (error) throw error;
      return data ?? [];
    },
    [segmentId],
  );

  const exportEmployees = async (): Promise<void> => {
    setLoading(true);
    try {
      const rows: EmployeeRow[] = [];
      let afterId: UUID | null = null;
      while (true) {
        const page = await loadPage(afterId);
        if (!page) throw new Error("Could not load employee export");
        rows.push(...page.map(({ data, id }) => ({ ...data, id })));
        if (page.length < 1000) break;
        afterId = page.at(-1)?.id ?? null;
        if (!afterId) break;
      }
      serveCsv(
        Papa.unparse({ fields, data: rows.map((row) => fields.map((field) => row[field])) }),
        `employees-${new Date().toISOString()}.csv`,
      );
    } catch (error) {
      toast.error("Failed to export employees");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      data-analytics-id="export-employees-csv"
      loading={loading}
      disabled={fields.length === 0}
      startIcon={<FileDownload />}
      onClick={() => void exportEmployees()}
    >
      Download as CSV
    </Button>
  );
};

function valueFormatter<T>(value: T): T | string {
  return value ?? "";
}
